/**
 * Where a token sits in the tower.
 *
 * Terraforms places every parcel inside a twenty-level "Hypercastle", and that placement is
 * what makes a 3D view of the whole collection possible at all: a flat list of ids has no
 * geometry, so there is nothing to walk through. The community explorers do not invent the
 * structure, they *read* it. This file is our equivalent, and it exists so `apps/tower` is
 * arranging the collection rather than decorating it.
 *
 * **Every floor is a different size, and that is the whole point.** The reference tower's
 * silhouette — a four-wide foot, stepped slabs, two narrow necks, a wide canopy near the top,
 * a spire — is not styling applied to a uniform stack. It is the census: level 1 holds 16
 * parcels and level 13 holds 2304, so the shape is what the counts *are*. Measured from
 * EnterDream's own `3d/data.js`, the Hypercastle's per-level footprints are
 *
 *     [4, 8, 8, 16, 16, 24, 24, 24, 16, 32, 32, 16, 48, 48, 24, 24, 16, 8, 8, 4]
 *
 * whose squares sum to 11104 — its `maxSupply`, exactly, with the 1195 gaps being unminted
 * tokens rather than structure. `LEVEL_SIDES` below is that profile resampled onto sixteen
 * levels (nearest level, which is what keeps the two necks: an averaging resample smooths them
 * away and the tower turns into a cone), scaled so the squares sum to our 4096, and snapped to
 * multiples of four. It lands 1.65 parcels rms from the scaled reference and hits 4096 exactly.
 *
 * **Ids ascend with the floor.** `placeToken` walks a sixteen-entry prefix table instead of
 * slicing nibbles, which costs a bounded loop and buys three things: a floor is a contiguous id
 * range, so `tools/atlas.ts` pages of 256 arrive in bottom-to-top order and the tower can grow
 * upward while a visitor watches; the level trait is monotone in the id; and the census can
 * change without any other file learning a new rule.
 *
 * **Level 0 is the ground.** Counting up from the floor means `level` and height agree in
 * sign, so a viewer never has to flip an axis. Terraforms numbers its levels from 1; ours are
 * 0-based like every other index in this engine, and `apps/tower` adds one only for display.
 *
 * **Floors are centred, not corner-aligned.** The reference's contract offsets each level by
 * `(48 - side) / 2` — checked: every level's structure-space centre is the same point — so the
 * stack shares one vertical axis. `levelOrigin` is that offset for our widest floor.
 *
 * **This is placement, not art.** Nothing in `art/traits.ts` reads these coordinates, so the
 * 4096 renders are byte-identical whether or not the tower exists, and re-shaping the tower
 * costs nothing but the tower. Gating biome or palette by level — the way Terraforms constrains
 * which zones appear at which elevation — would give the tower visible strata, and it would also
 * change every token. That is an art decision with a blast radius, so it is deliberately *not*
 * taken here.
 */

import { MAX_SUPPLY } from "../constants.js";

/** Stacked floors, bottom to top. */
export const TOWER_LEVELS = 16;

/**
 * Parcels along each axis of each floor, ground first.
 *
 * Read it as a silhouette: `4 4` foot, stepping out to `16 16`, a `12` neck, the `24 24` slab,
 * a second `12` neck, the `32` canopy, then `20 16 12 8` tapering to a `4` spire. The squares
 * sum to `MAX_SUPPLY`; `validatePlacement` enforces that, because a census that does not add up
 * is a tower with a hole or a token with nowhere to stand.
 */
export const LEVEL_SIDES: readonly number[] = [4, 4, 8, 12, 16, 16, 12, 24, 24, 12, 32, 20, 16, 12, 8, 4];

/** Parcels on each floor: `side * side`. */
export const LEVEL_COUNTS: readonly number[] = LEVEL_SIDES.map((s) => s * s);

/** First token id on each floor, so `placeToken` is a walk over sixteen numbers. */
export const LEVEL_BASE: readonly number[] = LEVEL_COUNTS.reduce<number[]>(
  (acc, n, i) => [...acc, (acc[i] ?? 0) + n],
  [0],
).slice(0, TOWER_LEVELS);

/** The widest floor. Every narrower floor is centred inside this footprint. */
export const TOWER_SPAN: number = LEVEL_SIDES.reduce((a, s) => (s > a ? s : a), 0);

/**
 * Tokens per atlas page, and the page's tile grid.
 *
 * A page is 256 ids because that is one 2048x2048 PNG of 128px tiles — the unit `tools/atlas.ts`
 * writes and the tower streams. It is deliberately *not* a floor: floors run from 16 parcels to
 * 1024 and a per-floor texture would be 64 KB at the foot and 16 MB at the canopy.
 */
export const PAGE_BITS = 8;
export const PARCELS_PER_PAGE = 1 << PAGE_BITS;
export const ATLAS_PAGES = MAX_SUPPLY / PARCELS_PER_PAGE;
export const TILE_BITS = PAGE_BITS / 2;
export const TILE_GRID = 1 << TILE_BITS;

/** A token's address in the tower. */
export interface Placement {
  /** 0 at the ground, `TOWER_LEVELS - 1` at the top. */
  readonly level: number;
  /** Column on the floor, 0..`levelSide(level) - 1`. */
  readonly x: number;
  /** Row on the floor, 0..`levelSide(level) - 1`. */
  readonly y: number;
}

/** Parcels along one axis of a floor. */
export function levelSide(level: number): number {
  const side = LEVEL_SIDES[level];
  if (side === undefined) throw new Error(`level ${level} is outside the tower`);
  return side;
}

/** How many parcels stand on a floor. */
export function levelCount(level: number): number {
  const side = levelSide(level);
  return side * side;
}

/** The first token id on a floor. */
export function levelBase(level: number): number {
  const base = LEVEL_BASE[level];
  if (base === undefined) throw new Error(`level ${level} is outside the tower`);
  return base;
}

/**
 * Where a floor's grid starts inside the widest footprint.
 *
 * `(TOWER_SPAN - side) / 2`, the same expression the reference contract uses, which is what puts
 * every floor's centre on one vertical axis. All sides are multiples of four, so this is always
 * a whole number of parcels and a floor never lands on a half cell.
 */
export function levelOrigin(level: number): number {
  return (TOWER_SPAN - levelSide(level)) / 2;
}

/** The floor a token stands on. */
export function tokenLevel(id: number): number {
  if (!Number.isInteger(id) || id < 0 || id >= MAX_SUPPLY) {
    throw new Error(`token id ${id} is outside 0..${MAX_SUPPLY - 1}`);
  }
  // Sixteen floors, so a walk beats a binary search and reads as the prefix table it is.
  let level = TOWER_LEVELS - 1;
  while (level > 0 && id < (LEVEL_BASE[level] ?? 0)) level--;
  return level;
}

/** Where a token sits on its floor. */
export function placeToken(id: number): Placement {
  const level = tokenLevel(id);
  const local = id - levelBase(level);
  const side = levelSide(level);
  return { level, x: Math.floor(local / side), y: local % side };
}

/** The inverse of `placeToken`. */
export function parcelId(level: number, x: number, y: number): number {
  const side = levelSide(level);
  if (!Number.isInteger(x) || x < 0 || x >= side) throw new Error(`x ${x} is off floor ${level}`);
  if (!Number.isInteger(y) || y < 0 || y >= side) throw new Error(`y ${y} is off floor ${level}`);
  return levelBase(level) + x * side + y;
}

/** Which atlas page carries a token's tile. */
export function tokenPage(id: number): number {
  return id >> PAGE_BITS;
}

/**
 * Where a token's tile sits inside its atlas page.
 *
 * The page's 256 ids fill a 16x16 grid of tiles in id order, so this is two shifts and a mask
 * and the viewer's UV lookup is arithmetic rather than a table. Note this grid has nothing to
 * do with the floor's grid — a page can straddle floors.
 */
export function tokenTile(id: number): { readonly x: number; readonly y: number } {
  const slot = id & (PARCELS_PER_PAGE - 1);
  return { x: slot >> TILE_BITS, y: slot & (TILE_GRID - 1) };
}

/**
 * Structural guard, run by the test suite.
 *
 * The property that matters is *bijection*: every id maps to a distinct cell and every cell
 * maps back to its id. A placement scheme that loses a token puts a hole in the tower, and
 * one that collides puts two birds in one parcel — both are silent, and both are visible
 * only as a rendering artefact three layers downstream. With a census the arithmetic can also
 * fail in a second way the old cube could not: the floors can simply not add up.
 */
export function validatePlacement(): void {
  if (LEVEL_SIDES.length !== TOWER_LEVELS) {
    throw new Error(`the census lists ${LEVEL_SIDES.length} floors, not ${TOWER_LEVELS}`);
  }
  const capacity = LEVEL_COUNTS.reduce((a, n) => a + n, 0);
  if (capacity !== MAX_SUPPLY) {
    throw new Error(`the floors hold ${capacity} parcels but the supply is ${MAX_SUPPLY}`);
  }
  if (new Set(LEVEL_SIDES).size < 2) throw new Error("every floor is the same size: that is not a tower");
  for (const [level, side] of LEVEL_SIDES.entries()) {
    if (!Number.isInteger(side) || side < 2) throw new Error(`floor ${level} has a side of ${side}`);
    if (side % 2 !== 0) throw new Error(`floor ${level} has an odd side, so it cannot be centred`);
    if (side > TOWER_SPAN) throw new Error(`floor ${level} is wider than TOWER_SPAN`);
    if ((TOWER_SPAN - side) % 2 !== 0) throw new Error(`floor ${level} cannot be centred on whole parcels`);
  }
  if (MAX_SUPPLY % PARCELS_PER_PAGE !== 0) throw new Error("the supply does not divide into whole atlas pages");
  if (TILE_GRID * TILE_GRID !== PARCELS_PER_PAGE) throw new Error("TILE_GRID does not describe a page");

  const seen = new Uint8Array(MAX_SUPPLY);
  let previous = -1;
  for (let id = 0; id < MAX_SUPPLY; id++) {
    const p = placeToken(id);
    if (p.level < previous) throw new Error(`id ${id} went down a floor: ids must ascend with the tower`);
    previous = p.level;
    const side = levelSide(p.level);
    if (p.x < 0 || p.x >= side || p.y < 0 || p.y >= side) throw new Error(`id ${id} landed off its floor`);
    const back = parcelId(p.level, p.x, p.y);
    if (back !== id) throw new Error(`id ${id} round-tripped to ${back}`);
    const slot = levelBase(p.level) + p.x * side + p.y;
    if (seen[slot] === 1) throw new Error(`two tokens share parcel ${p.level}/${p.x}/${p.y}`);
    seen[slot] = 1;
  }
  for (let slot = 0; slot < MAX_SUPPLY; slot++) {
    if (seen[slot] !== 1) throw new Error(`parcel slot ${slot} is empty`);
  }
  if (tokenLevel(0) !== 0) throw new Error("token 0 is not on the ground floor");
  if (tokenLevel(MAX_SUPPLY - 1) !== TOWER_LEVELS - 1) throw new Error("the last token is not on the top floor");
  if (tokenPage(0) !== 0 || tokenPage(MAX_SUPPLY - 1) !== ATLAS_PAGES - 1) throw new Error("atlas paging is off");
}
